/** Renders a single day cell in the horizon month view with task indicators and droppable support. */
import { useDroppable } from '@dnd-kit/core';
import { format, isSameDay, isToday } from 'date-fns';
import { Box } from '@mui/material';
import GridTaskItem from '@/features/planning/components/shared/GridTaskItem';
import GCalEventItem from '@/features/planning/components/shared/GCalEventItem';
import CapacityDot from '@/features/planning/components/shared/CapacityDot';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { DAY_VIEW_CAPACITY_HOURS } from '@/features/planning/config/planningConfig';
import {
  filterDayWorkUnits,
  getCapacityDurationMinutes,
  isSchedulableTask,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import { formatShortMonthAbbrev } from '@/i18n/getDateFnsLocale';

const MINUTES_PER_HOUR = 60;
const DAY_CAPACITY_MINUTES = DAY_VIEW_CAPACITY_HOURS * MINUTES_PER_HOUR;

const FONT_WEIGHT_BOLD = 700;
const FONT_WEIGHT_NORMAL = 400;

const toDate = (date) =>
  date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);

const MonthDay = ({ date, tasks = [], gcalEvents = [] }) => {
  const { tasksMap } = useTasksContext();
  const { setNodeRef, isOver } = useDroppable({
    id: `month-day-${format(date, 'yyyy-MM-dd')}`,
    data: {
      type: 'month-day',
      date: format(date, 'yyyy-MM-dd'),
    },
  });

  const isCurrentDay = isToday(date);
  const tasksForDay = tasks.filter((task) => {
    if (!task.date || task.completed) return false;
    if (!isSchedulableTask(task, tasksMap)) return false;
    return isSameDay(toDate(task.date), date);
  });
  const visibleTasksForDay = filterDayWorkUnits(tasksForDay, tasksMap);
  const plannedMins = visibleTasksForDay.reduce(
    (sum, task) => sum + getCapacityDurationMinutes(task, tasksForDay, tasksMap),
    0,
  );

  return (
    <Box
      data-testid="month-day"
      sx={{
        border: 1,
        borderColor: isCurrentDay ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: isOver ? 'action.hover' : 'background.paper',
        p: '4px',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Box
          sx={{
            fontSize: '0.72em',
            fontWeight: isCurrentDay ? FONT_WEIGHT_BOLD : FONT_WEIGHT_NORMAL,
            color: isCurrentDay ? 'primary.main' : 'text.secondary',
          }}
        >
          {format(date, 'd')}
          {date.getDate() === 1 ? ` ${formatShortMonthAbbrev(date)}` : ''}
        </Box>
        <CapacityDot planned={plannedMins} max={DAY_CAPACITY_MINUTES} />
      </Box>
      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minHeight: 0,
        }}
      >
        {visibleTasksForDay.map((task) => (
          <GridTaskItem key={task.id} task={task} dense />
        ))}
        {gcalEvents.map((event) => (
          <GCalEventItem key={event.id} event={event} dense />
        ))}
      </Box>
    </Box>
  );
};

export default MonthDay;
